import {
  BufferGeometry,
  Group,
  Line,
  Line3,
  LineBasicMaterial,
  MathUtils,
  Mesh,
  MeshBasicMaterial,
  SphereGeometry,
  Vector3,
} from "three";
import { KeyboardKeyHold } from "hold-event";

import { Context } from "../context";

import { CameraController } from "./controller";
import { Collider } from "../world/stadium/collider";
import { Capsule, LineGeometry } from "three-stdlib";

export interface DeferredCameraTransition {
  cb: () => void;
  remainingFrames: number;
}

export interface CameraModeOptions {
  focusedID?: number;
}

/**
 * Strategy class for CameraController. We typically use the CameraControls
 * from [here](https://github.com/yomotsu/camera-controls/), but that isn't
 * a requirement (you can update the camera positions directly if you prefer).
 *
 * The strategy is responsible for setting the state required for it's behaviour
 * (in the init() method), and also for reverting that state (in the teardown()
 * method). An optional update() method is useful for cameras that depend on state
 * in the scene / beyond the user's mouse controls.
 */
export class ControllerStrategy {
  registered?: CameraController;
  lastFrameNum?: number;

  protected deferedTransitions: Set<DeferredCameraTransition> = new Set();

  init(_context: Context, _options?: CameraModeOptions): ControllerStrategy {
    return this;
  }

  teardown(): void {}
  update(_deltaS: number, _gameTimeDelta: number, _frameNum?: number): void {}

  protected resolveDeferals(frameNum: number) {
    if (this.lastFrameNum == undefined) {
      return;
    }

    // TODO: gracefully handle seek events here?
    for (const transition of this.deferedTransitions) {
      transition.remainingFrames -= frameNum - this.lastFrameNum;
      if (transition.remainingFrames <= 0) {
        transition.cb();
        this.deferedTransitions.delete(transition);
      }
    }
  }
}

/**
 * Ensures that a camera is initialised above a certain height
 */
export class MinHeightControllerStrategy extends ControllerStrategy {
  minHeight = 3;
  temp = new Vector3();

  init(context: Context): ControllerStrategy {
    super.init(context);

    const controls = this.registered?.controls;
    if (controls) {
      controls.getTarget(this.temp);

      if (this.temp.y < this.minHeight) {
        this.temp.setY(this.minHeight);
        controls.setTarget(this.temp.x, this.temp.y, this.temp.z);
      }
    }

    return this;
  }
}

/**
 * Map controls mean clicking and dragging upwards on the scene moves you
 * forwards on the ground plane
 */
export class MapControllerStrategy extends ControllerStrategy {
  init(context: Context): ControllerStrategy {
    super.init(context);

    const controls = this.registered?.controls;
    if (controls) {
      controls.verticalDragToForward = true;
    }

    return this;
  }

  teardown() {
    if (this.registered?.controls) {
      this.registered.controls.verticalDragToForward = false;
    }
    super.teardown();
  }
}

/**
 * Simple collision class to ensure camera doesn't clip through ground plane.
 */
export class GroundCollisionControllerStrategy extends ControllerStrategy {
  groundLevel = 2;
  pos = new Vector3();
  offset = new Vector3();
  target = new Vector3();

  prevPos = 0;
  prevOffset = 0;

  update(_deltaS: number, _gameTimeDelta: number, _frameNum?: number) {
    const controls = this.registered?.controls;

    if (controls) {
      controls.getPosition(this.pos);
      controls.getFocalOffset(this.offset);
      controls.getTarget(this.target);

      const delta = this.pos.y - this.offset.y - this.groundLevel;

      if (delta < 0) {
        if (this.pos.y != this.prevPos) {
          this.pos.y = this.prevPos;
          controls.setPosition(this.pos.x, this.pos.y, this.pos.z);
        }
        if (this.offset.y != this.prevOffset) {
          this.offset.y = this.prevOffset;
          controls.setFocalOffset(this.offset.x, this.offset.y, this.offset.z);
        }
      }

      this.prevPos = this.pos.y;
      this.prevOffset = this.offset.y;
    }
  }
}

/**
 * Keyboard control defaults. Can be subclassed to alter behaviour (reimplement
 * moveRight() for example).
 */
export class KeyboardHoldControllerStrategy extends ControllerStrategy {
  // TODO: work out how to type eventListeners
  keys: { [key: string]: [any, KeyboardKeyHold] } = {};

  private KEYCODE = {
    W: 87,
    A: 65,
    S: 83,
    D: 68,
    Q: 81,
    R: 82,
    E: 69,
    F: 70,
  };

  init(context: Context): ControllerStrategy {
    super.init(context);

    this.keys.a = [
      (event: any) => event && this.moveRight(-0.01 * event.deltaTime),
      new KeyboardKeyHold(this.KEYCODE.A, 16.666),
    ];
    this.keys.d = [
      (event: any) => event && this.moveRight(0.01 * event.deltaTime),
      new KeyboardKeyHold(this.KEYCODE.D, 16.666),
    ];
    this.keys.w = [
      (event: any) => event && this.moveUp(-0.01 * event.deltaTime),
      new KeyboardKeyHold(this.KEYCODE.W, 16.666),
    ];
    this.keys.s = [
      (event: any) => event && this.moveUp(0.01 * event.deltaTime),
      new KeyboardKeyHold(this.KEYCODE.S, 16.666),
    ];
    this.keys.left = [
      (event: any) =>
        event && this.rotateSide(-0.1 * MathUtils.DEG2RAD * event.deltaTime),
      new KeyboardKeyHold(this.KEYCODE.Q, 100),
    ];
    this.keys.right = [
      (event: any) =>
        event && this.rotateSide(0.1 * MathUtils.DEG2RAD * event.deltaTime),
      new KeyboardKeyHold(this.KEYCODE.E, 100),
    ];
    this.keys.up = [
      (event: any) =>
        event && this.rotateUp(-0.05 * MathUtils.DEG2RAD * event.deltaTime),
      new KeyboardKeyHold(this.KEYCODE.R, 100),
    ];
    this.keys.down = [
      (event: any) =>
        event && this.rotateUp(0.05 * MathUtils.DEG2RAD * event.deltaTime),
      new KeyboardKeyHold(this.KEYCODE.F, 100),
    ];

    for (const name in this.keys) {
      this.keys[name][1].addEventListener("holding", this.keys[name][0]);
    }

    return this;
  }

  moveRight(delta: number) {
    const controls = this.registered?.controls;
    if (!controls) {
      return;
    }

    controls.truck(delta, 0, false);
  }

  moveUp(delta: number) {
    const controls = this.registered?.controls;
    if (!controls) {
      return;
    }

    controls.truck(0, delta, false);
  }

  rotateSide(delta: number) {
    const controls = this.registered?.controls;
    if (!controls) {
      return;
    }

    controls.rotate(delta, 0, true);
  }

  rotateUp(delta: number) {
    const controls = this.registered?.controls;
    if (!controls) {
      return;
    }

    controls.rotate(0, delta, true);
  }

  teardown() {
    super.teardown();

    for (const name in this.keys) {
      this.keys[name][1].removeEventListener("holding", this.keys[name][0]);
    }
  }
}

/**
 * Makes keyboard controls consistent with mouse controls for MAP camera mode
 */
export class MapKeyboardHoldControllerStrategy extends KeyboardHoldControllerStrategy {
  moveUp(delta: number) {
    const controls = this.registered?.controls;
    if (!controls) {
      return;
    }

    controls.forward(-delta, false);
  }
}

export class StadiumCollisionControllerStrategy extends ControllerStrategy {
  private pos = new Vector3();
  private target = new Vector3();

  private targetHeightTolerance = 0.5;
  // TODO: do we need to lazy instantiate this to cover different ground
  //       heights in different stadia?
  private refHeight = 5;
  private down = new Vector3(0, -1, 0);
  private delta = new Vector3();
  private goodTarget?: Vector3;

  private collider?: Collider;

  private contextRef?: Context;

  private fallenBack = false;

  init(context: Context): ControllerStrategy {
    super.init(context);

    this.contextRef = context;

    return this;
  }

  update(deltaS: number, gameTimeDelta: number, frameNum?: number): void {
    if (!this.contextRef?.state.stadiumCollision) return;

    const controller = this.registered;
    const controls = this.registered?.controls;
    const context = this.registered?.contextRef;

    if (controls) {
      if (!this.collider && context?.world?.stadium?.collider) {
        // lazy add collider and configure collision meshes
        this.collider = context.world.stadium.collider;
        controls.colliderMeshes.push(this.collider.collider);
      }

      controls.getPosition(this.pos);
      controls.getTarget(this.target);
      if (this.collider && controller) {
        controller.targetSphere.mesh.position.copy(this.target);

        this.target.y = this.refHeight;

        const intersection = this.collider.raycast(this.target, this.down);
        if (
          !intersection ||
          Math.abs(intersection.distance - this.refHeight) >
            this.targetHeightTolerance
        ) {
          if (this.goodTarget) {
            this.delta.subVectors(this.goodTarget, this.target);
            this.pos.add(this.delta);
            this.target.copy(this.goodTarget);

            controls.setLookAt(
              this.pos.x,
              this.pos.y,
              this.pos.z,
              this.target.x,
              this.target.y,
              this.target.z,
              false
            );
          }
        } else {
          if (!this.goodTarget) {
            this.goodTarget = new Vector3();
          }

          this.goodTarget.copy(this.target);
        }

        // teleport behind home if out-of-bounds
        // using actual camera position
        if (!this.collider.inBounds(controls.camera.position)) {
          console.warn("camera rescue mode", this.pos, this.target);
          controls.setLookAt(0, 10, 30, 0, 5, -40, false);
        }
      } else {
        if (!this.fallenBack) {
          console.warn("falling back to ground collision");
          this.registered?.addAuxStrategy(GroundCollisionControllerStrategy);

          this.fallenBack = true;
        }
      }
    }
  }

  teardown(): void {
    super.teardown();

    if (this.fallenBack) {
      this.registered?.removeAuxStrategy(GroundCollisionControllerStrategy);
    }

    const controller = this.registered;
    if (controller) {
      if (controller.controls) {
        controller.controls.colliderMeshes = [];
      }
    }
  }
}
